import React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import { format, isToday } from 'date-fns';

import Button from '@dashboard/components/Button';
import { toast } from '@dashboard/context/ToastContext';
import usePage from '@dashboard/hooks/usePage';

const NOUN = {
  checkout: 'checkout',
  event: 'event',
  form: 'form',
};

function formatEnabledAt(value) {
  if (!value) {
    return null;
  }

  const at = new Date(value);

  if (Number.isNaN(at.getTime())) {
    return null;
  }

  const time = format(at, 'h:mm a');

  return isToday(at)
    ? 'On since ' + time + ' today'
    : 'On since ' + time + ' on ' + format(at, 'd MMM');
}

const PREVIEW = 'preview';
const EVERYWHERE = 'everywhere';

function ScopeOption({ value, title, description, isSelected, tone = 'neutral', children }) {
  return (
    <div
      className={clsx('relative rounded-lg border p-4 transition-colors', {
        'border-chocolate-600 bg-chocolate-50 dark:bg-chocolate-500/15':
          isSelected && tone === 'warning',
        'border-foreground': isSelected && tone === 'neutral',
        'border-input': !isSelected,
      })}
    >
      <RadioGroupPrimitive.Item
        value={value}
        className="flex w-full cursor-pointer items-start gap-3 text-left outline-none after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-ring disabled:cursor-default"
      >
        <span
          aria-hidden="true"
          className={clsx(
            'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
            isSelected && tone === 'warning' && 'border-chocolate-700',
            isSelected && tone === 'neutral' && 'border-foreground',
            !isSelected && 'border-input',
          )}
        >
          <RadioGroupPrimitive.Indicator
            className={clsx(
              'size-2 rounded-full',
              tone === 'warning' ? 'bg-chocolate-700' : 'bg-foreground',
            )}
          />
        </span>

        <span className="block">
          <span className="block text-sm font-medium text-foreground">{title}</span>
          <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
        </span>
      </RadioGroupPrimitive.Item>

      {children}
    </div>
  );
}

export default function TestModeScope() {
  const { page, updatePage } = usePage();
  const isEverywhere = page?.testmode === true;
  const noun = NOUN[page?.type] || 'checkout';
  const isForm = page?.type === 'form';
  const activity = isForm ? 'test submissions' : 'test payments';
  const liveActivity = isForm ? 'real submissions' : 'real payments';
  const enabledAt = formatEnabledAt(page?.testmodeEnabledAt);

  async function setScope(value) {
    const testmode = value === EVERYWHERE;

    if (testmode === isEverywhere) {
      return;
    }

    const { error } = await updatePage.mutateAsync({ testmode });

    if (error) {
      toast({ title: error, type: 'error' });
      return;
    }

    toast({
      title: testmode
        ? 'Test mode on. Test payments only.'
        : 'Test mode off. This ' + noun + ' is taking real payments.',
      type: 'success',
    });
  }

  return (
    <div className="my-4">
      <h3 className="mb-3 text-sm font-medium text-foreground">Where should {activity} work?</h3>

      <RadioGroupPrimitive.Root
        value={isEverywhere ? EVERYWHERE : PREVIEW}
        onValueChange={setScope}
        disabled={updatePage.isPending}
        className="space-y-3"
      >
        <ScopeOption
          value={PREVIEW}
          isSelected={!isEverywhere}
          title="Preview only"
          description={'Live ' + noun + 's keep accepting ' + liveActivity + '.'}
        />

        <ScopeOption
          value={EVERYWHERE}
          isSelected={isEverywhere}
          tone="warning"
          title="Everywhere"
          description={
            isForm
              ? 'Every submission is recorded as a test until test mode is off.'
              : 'Real payments are declined until test mode is off.'
          }
        >
          {isEverywhere && (
            <div className="mt-3 flex items-center gap-3 pl-7">
              <Button
                appearance="primary"
                size="small"
                className="relative"
                disabled={updatePage.isPending}
                onClick={() => setScope(PREVIEW)}
              >
                Turn off test mode
              </Button>

              {enabledAt && (
                <span className="text-sm text-chocolate-700 dark:text-chocolate-300">
                  {enabledAt}
                </span>
              )}
            </div>
          )}
        </ScopeOption>
      </RadioGroupPrimitive.Root>
    </div>
  );
}
